import api from './Api';

export interface SubscriptionDto {
  id: number;
  userId: number;
  username: string;
  plan: string;
  status: string;
  startDate: string;
  endDate: string;
  autoRenew: boolean;
  infiniteSubscription: boolean;
  remainingDays: number;
  lastRenewalDate?: string;
  cancelledDate?: string;
  notes?: string;
  lastModifiedBy?: string;
  lastModifiedDate?: string;
}

export interface SubscriptionEvent {
  id: number;
  userId: number;
  username: string;
  eventType: string;
  oldPlan?: string;
  newPlan?: string;
  daysDelta?: number;
  performedBy: string;
  eventTime: string;
  notes?: string;
}

export interface SubscriptionStats {
  totalCreated: number;
  totalRenewals: number;
  totalCancelled: number;
  totalExpired: number;
  totalExtended: number;
  distinctUsersCreated: number;
  distinctUsersRenewed: number;
  last30DaysTotal: number;
  last30DaysCreated: number;
  last30DaysRenewals: number;
  last30DaysCancelled: number;
  last30DaysExpired: number;
  monthlyNewSubscriptions: Array<{ year: number; month: number; count: number }>;
  monthlyRenewals: Array<{ year: number; month: number; count: number }>;
}

export const SubscriptionApi = {
  // Create a new subscription for a user (Admin only)
  create: (userId: number, plan: string, autoRenew: boolean) =>
    api.post<SubscriptionDto>('/admin/subscriptions', { userId, plan, autoRenew }),

  // Get current user's subscription
  getMySubscription: () =>
    api.get<SubscriptionDto>('/subscription/me'),
  
  // Get subscription by user ID (Admin only)
  getByUserId: (userId: number) => 
    api.get<SubscriptionDto>(`/admin/subscriptions/user/${userId}`),
  
  // Extend subscription by custom number of days (Admin only)
  extend: (userId: number, days: number) =>
    api.post<SubscriptionDto>(`/admin/subscriptions/${userId}/extend?days=${days}`),
  
  // Renew subscription (extends by plan duration)
  renew: (userId: number) =>
    api.post<SubscriptionDto>(`/admin/subscriptions/${userId}/renew`),
  
  // Update subscription plan
  updatePlan: (userId: number, plan: string) =>
    api.put<SubscriptionDto>(`/admin/subscriptions/${userId}/plan`, { plan }),
  
  // Set infinite subscription
  setInfinite: (userId: number, infinite: boolean) =>
    api.put<SubscriptionDto>(`/admin/subscriptions/${userId}/infinite?infinite=${infinite}`),
  
  // Set exact days remaining (supports increase and decrease)
  setDays: (userId: number, days: number) =>
    api.put<SubscriptionDto>(`/admin/subscriptions/${userId}/set-days?days=${days}`),

  // Cancel subscription
  cancel: (userId: number) =>
    api.post<SubscriptionDto>(`/admin/subscriptions/${userId}/cancel`),
  
  // Update notes
  updateNotes: (userId: number, notes: string) =>
    api.put<SubscriptionDto>(`/admin/subscriptions/${userId}/notes`, notes, {
      headers: { 'Content-Type': 'text/plain' }
    }),

  // --- History & Statistics ---

  // All subscription events (admin)
  getHistory: () =>
    api.get<SubscriptionEvent[]>('/admin/subscriptions/history'),

  // Events for a specific user (admin)
  getUserHistory: (userId: number) =>
    api.get<SubscriptionEvent[]>(`/admin/subscriptions/history/${userId}`),

  // Aggregate statistics (admin)
  getStatistics: () =>
    api.get<SubscriptionStats>('/admin/subscriptions/statistics'),
};
